import { useRect } from '@reach/rect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useImageContentControlsContext } from '~/components/AssetModalV2/Content/ImageContentControlsContextProvider';
import { PositionedImage } from '~/components/EditMode/components/BoundingBoxLayerKonva/services/PositionedImage';
import { type EditModeOption } from '~/components/EditMode/constants';
import { MoveMode, RefocusMode, SelectionMode, useEditMode } from '~/components/EditMode/EditModeProvider';
import { centralizedClipDimensionsSelector } from '~/store/centralizedClip/selectors';

const EDIT_MODE_SUPPORTS_BOUNDING_BOX: EditModeOption['id'][] = ['removeObject', 'crop', 'outpaint'];

export const useEditModeBoundingBox = () => {
  const {
    currentEditModeOption,
    isEditModeEnabled,
    assetTransformStatus,
    boundingBox,
    visualizerRef,
    imageRef,
    setImageOffset,
  } = useEditMode();

  const { width: originalImageWidth, height: originalImageHeight } = useSelector(centralizedClipDimensionsSelector);
  const { updateZoom } = useImageContentControlsContext();

  const showBoundingBox = useMemo(() => {
    if (!currentEditModeOption) {
      return false;
    }

    return (
      EDIT_MODE_SUPPORTS_BOUNDING_BOX.includes(currentEditModeOption.id) &&
      isEditModeEnabled &&
      assetTransformStatus === 'idle'
    );
  }, [currentEditModeOption, isEditModeEnabled, assetTransformStatus]);

  const visualizerRect = useRect(visualizerRef);
  const containerRect = useRect(boundingBox.containerRef);
  const imageRect = useRect(imageRef);

  const [switchingModes, setSwitchingModes] = useState(false);

  const positionedImage = useMemo(() => {
    if (!containerRect || !imageRect) {
      return null;
    }

    const imageContainer = imageRef.current?.parentElement;
    const initialImageX =
      imageContainer && containerRect ? imageContainer.getBoundingClientRect().left - containerRect.left : null;
    const initialImageY =
      imageContainer && containerRect ? imageContainer.getBoundingClientRect().top - containerRect.top : null;

    if (initialImageX === null || initialImageY === null) {
      return null;
    }

    return new PositionedImage({
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
      imageWidth: imageRect.width,
      imageHeight: imageRect.height,
      imageX: imageRect.left - containerRect.left,
      imageY: imageRect.top - containerRect.top,
      originalImageWidth,
      originalImageHeight,
      initialImageX,
      initialImageY,
      setImagePosition: setImageOffset,
      imageScale: boundingBox.scale,
      setImageScale: boundingBox.setScale,
    });
  }, [
    imageRef.current?.parentElement,
    containerRect,
    imageRect,
    originalImageHeight,
    originalImageWidth,
    boundingBox.scale,
    boundingBox.setScale,
    setImageOffset,
  ]);

  const reset = useCallback(() => {
    boundingBox.setBox(null);
    boundingBox.setShowGuideGrid(false);
    boundingBox.setShowDimensions(false);
    boundingBox.setAspectRatio(undefined);
    boundingBox.setSelectionMode(SelectionMode.FREE);
    boundingBox.setMoveMode(MoveMode.BOX);
    boundingBox.setRefocusMode(RefocusMode.NONE);
    boundingBox.setScale(1);
    setImageOffset({ x: 0, y: 0 });
  }, [
    boundingBox.setAspectRatio,
    boundingBox.setBox,
    boundingBox.setShowDimensions,
    boundingBox.setMoveMode,
    boundingBox.setRefocusMode,
    boundingBox.setScale,
    boundingBox.setSelectionMode,
    boundingBox.setShowGuideGrid,
    setImageOffset,
  ]);

  const setCropMode = useCallback(() => {
    boundingBox.setSelectionMode(SelectionMode.INSIDE);
    boundingBox.setMoveMode(MoveMode.BOX);
    boundingBox.setRefocusMode(RefocusMode.NONE);
    boundingBox.setShowGuideGrid(true);
    boundingBox.setShowDimensions(false);
    boundingBox.setAspectRatio(undefined);
    boundingBox.setScale(1);
    setImageOffset({ x: 0, y: 0 });
  }, [
    boundingBox.setAspectRatio,
    boundingBox.setMoveMode,
    boundingBox.setRefocusMode,
    boundingBox.setScale,
    boundingBox.setSelectionMode,
    boundingBox.setShowGuideGrid,
    boundingBox.setShowDimensions,
    setImageOffset,
  ]);

  const setRemoveObjectMode = useCallback(() => {
    boundingBox.setSelectionMode(SelectionMode.INSIDE);
    boundingBox.setMoveMode(MoveMode.BOX);
    boundingBox.setRefocusMode(RefocusMode.NONE);
    boundingBox.setShowGuideGrid(true);
    boundingBox.setShowDimensions(false);
    boundingBox.setAspectRatio(undefined);
    boundingBox.setScale(1);
    setImageOffset({ x: 0, y: 0 });
  }, [
    boundingBox.setAspectRatio,
    boundingBox.setMoveMode,
    boundingBox.setRefocusMode,
    boundingBox.setScale,
    boundingBox.setSelectionMode,
    boundingBox.setShowGuideGrid,
    setImageOffset,
    boundingBox.setShowDimensions,
  ]);

  const setOutpaintMode = useCallback(() => {
    boundingBox.setBox(null);
    boundingBox.setSelectionMode(SelectionMode.OUTSIDE);
    boundingBox.setMoveMode(MoveMode.IMAGE);
    boundingBox.setRefocusMode(RefocusMode.BOX);
    boundingBox.setShowGuideGrid(true);
    boundingBox.setShowDimensions(true);
    boundingBox.setAspectRatio(undefined);
    boundingBox.setScale(1);
    setImageOffset({ x: 0, y: 0 });
    updateZoom(1);
  }, [
    boundingBox.setBox,
    boundingBox.setSelectionMode,
    boundingBox.setAspectRatio,
    boundingBox.setMoveMode,
    boundingBox.setRefocusMode,
    boundingBox.setScale,
    boundingBox.setShowGuideGrid,
    boundingBox.setShowDimensions,
    setImageOffset,
    updateZoom,
  ]);

  useEffect(() => {
    // Timeout from components/AssetModalV2/PrivateAssetModalDesktopLayout.tsx
    const SWITCHING_MODES_RESET_MS = 400;
    setSwitchingModes(true);
    setTimeout(() => {
      setSwitchingModes(false);
    }, SWITCHING_MODES_RESET_MS);

    if (currentEditModeOption?.id === 'crop') {
      setCropMode();
    } else if (currentEditModeOption?.id === 'removeObject') {
      setRemoveObjectMode();
    } else if (currentEditModeOption?.id === 'outpaint') {
      setOutpaintMode();
    } else {
      reset();
    }
  }, [currentEditModeOption, reset, setCropMode, setOutpaintMode, setRemoveObjectMode]);

  useIsomorphicLayoutEffect(() => {
    if (currentEditModeOption?.id === 'crop') {
      if (positionedImage && switchingModes) {
        boundingBox.setBox(positionedImage.image);
      }
    }
    if (currentEditModeOption?.id === 'outpaint') {
      if (positionedImage && switchingModes) {
        boundingBox.setBox(positionedImage.image);
      }
    }
  }, [switchingModes, boundingBox.setBox, positionedImage, currentEditModeOption?.id]);

  const containerDimensions = useMemo(() => {
    if (!visualizerRect || !imageRect) {
      return null;
    }

    // Each bar is Tailwind `h-16` (4rem) which equals 64px; there are two (top + bottom)
    // 10px padding for left and right
    return {
      containerWidth: `calc(${visualizerRect.width}px - 2 * 16px)`,
      containerHeight: `calc(${visualizerRect.height}px - 2 * 64px)`,
    };
  }, [imageRect, visualizerRect]);

  return {
    boundingBox: {
      show: showBoundingBox,
      ...boundingBox,
      containerDimensions,
      containerRect,
      positionedImage,
      reset,
    },
    visualizerRef,
    imageRef,
  };
};
